import { z } from "zod";
import { Complexity, TaskType } from "./schemas.js";
import { createOllamaGenerator } from "./ollama.js";

// Lazy-load the Anthropic SDK to avoid crashing when it's not installed
type Anthropic = import("@anthropic-ai/sdk").default;
async function getAnthropicClient(apiKey?: string): Promise<Anthropic> {
  const { default: AnthropicSDK } = await import("@anthropic-ai/sdk");
  return new AnthropicSDK({ apiKey: apiKey ?? process.env.ANTHROPIC_API_KEY });
}

export const AIComplexityResult = z.object({
  complexity: Complexity,
  taskTypes: z.array(TaskType),
  dependencies: z.array(z.string()),
  aiTasks: z.array(z.string()),
  rationale: z.string(),
});
export type AIComplexityResult = z.infer<typeof AIComplexityResult>;

const TASK_TYPE_VALUES = TaskType.options.join(", ");

const SYSTEM_PROMPT = `You are a technical complexity scorer for software features. Given a feature description, analyze it and return a JSON object with:

- complexity: One of "S", "M", "L", "XL"
  - S: Single file, simple change, <30 min
  - M: 2-5 files, typical feature, 1-3 hours
  - L: 5-15 files, multiple integrations, 3-8 hours
  - XL: 15+ files, system-wide changes, 8+ hours
- taskTypes: Array of applicable task types from: ${TASK_TYPE_VALUES}
- dependencies: Array of likely external dependencies or integrations (e.g., "OAuth provider", "database migration")
- aiTasks: Array of 2-5 discrete AI coding tasks (e.g., "scaffold-auth-flow", "write-api-tests")
- rationale: One sentence explaining the complexity assessment

Respond with ONLY a JSON object. No markdown, no explanation.`;

export type GenerateFunction = (systemPrompt: string, userPrompt: string) => Promise<string>;

export interface AIComplexityOptions {
  apiKey?: string;
  model?: string;
  client?: Anthropic;
  generate?: GenerateFunction;
  provider?: 'anthropic' | 'openai' | 'ollama' | 'custom';
  openaiApiKey?: string;
  ollamaUrl?: string;
  ollamaModel?: string;
}

/**
 * Score complexity using Claude Haiku for rich, accurate analysis.
 * Falls back to null if the SDK is unavailable or the API call fails.
 */
export async function scoreComplexityAI(
  description: string,
  options: AIComplexityOptions = {},
): Promise<AIComplexityResult | null> {
  try {
    const userPrompt = `Analyze the complexity of this feature:\n\n${description}`;
    let text: string;

    if (options.generate) {
      // Custom generate function — fully pluggable
      text = await options.generate(SYSTEM_PROMPT, userPrompt);
    } else if (options.provider === 'openai') {
      // OpenAI provider via dynamic import (openai package is optional)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const openaiModule = await import("openai" as any) as any;
      const OpenAI = openaiModule.default ?? openaiModule;
      const client = new OpenAI({ apiKey: options.openaiApiKey ?? process.env.OPENAI_API_KEY });
      const response = await client.chat.completions.create({
        model: options.model ?? "gpt-4o-mini",
        messages: [
          { role: "system", content: SYSTEM_PROMPT },
          { role: "user", content: userPrompt },
        ],
        max_tokens: 1024,
      });
      text = response.choices[0]?.message?.content ?? "";
    } else if (options.provider === 'ollama') {
      const generate = createOllamaGenerator({
        url: options.ollamaUrl,
        model: options.ollamaModel ?? options.model,
      });
      text = await generate(SYSTEM_PROMPT, userPrompt);
    } else {
      // Default: Anthropic provider
      const client = options.client ?? (await getAnthropicClient(options.apiKey));
      const model = options.model ?? "claude-haiku-4-5-20251001";

      const response = await client.messages.create({
        model,
        max_tokens: 1024,
        system: SYSTEM_PROMPT,
        messages: [
          { role: "user", content: userPrompt },
        ],
      });

      const textBlock = response.content.find((block) => block.type === "text");
      if (!textBlock || textBlock.type !== "text") {
        return null;
      }
      text = textBlock.text;
    }

    // Strip markdown wrapping if present
    let jsonText = text.trim();
    if (jsonText.startsWith("```")) {
      jsonText = jsonText.replace(/^```(?:json)?\n?/, "").replace(/\n?```$/, "");
    }

    const parsed = JSON.parse(jsonText);
    return AIComplexityResult.parse(parsed);
  } catch {
    // SDK not installed, API key missing, network error, parse error — all silent failures
    return null;
  }
}
